import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useSelector } from 'react-redux';

import IconMercadoPago from '@/assets/rechargeBalance/logo_mpago.png';
import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { RootState } from '@/redux/store';
import EditProfile from '@/sections/EditProfile';

import HowMuchRecharge from '../HowMuchRecharge';

interface BillingProps {
  requireInvoice: boolean;
  toProfile: () => void;
}

const Balance = () => {
  const { profile } = useSelector((state: RootState) => state.parcel);
  const [editProfile, setEditProfile] = useState(false);
  const [isSelectedMethod, setIsSelectedMethod] = useState({
    value: false,
    method: '',
  });
  const theme = useTheme();

  if (
    !(
      profile?.data?.profile &&
      profile?.data?.profile?.firstName?.length > 0 &&
      profile?.data?.profile?.lastName?.length > 0 &&
      profile?.data?.profile?.email?.length > 0 &&
      profile?.data?.profile?.phone?.length > 0
    )
  ) {
    return (
      <Box>
        <Typography>
          Identificamos que faltan algunos datos en tu cuenta. <br />
          Para poder realizar la recarga, por favor completa tu nombre, correo electrónico y
          teléfono.
          <br />
          <strong onClick={() => setEditProfile(true)}>
            Haz clic aquí para completar tus datos.
          </strong>
        </Typography>
        <GenericDrawer
          open={editProfile}
          onClose={() => setEditProfile(false)}
          title="Mis datos"
          disableEscapeKeyDown={true}
          iconTitle={<FilterIcon color="primary" />}
        >
          <EditProfile />
        </GenericDrawer>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: '1rem' }}>
        {profile.data.country === 'MEX' ? 'Elige un método de pago' : 'Método de pago'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: '1rem',
          [theme.breakpoints.down('xl')]: { flexDirection: 'column' },
          [theme.breakpoints.between('sm', 'lg')]: { flexDirection: 'row' },
        }}
      >
        {profile.data.country === 'MEX' && (
          <Button
            variant="contained"
            startIcon={<PaymentIcon sx={{ color: 'white' }} />}
            onClick={() => setIsSelectedMethod({ value: true, method: 'card' })}
          >
            Stripe
          </Button>
        )}
        {profile.data.country !== 'MEX' && (
          <Button
            variant="contained"
            startIcon={<img src={IconMercadoPago} alt="mercadoPago" />}
            onClick={() => setIsSelectedMethod({ value: true, method: 'mercadoPago' })}
          ></Button>
        )}
      </Box>
      <Box sx={{ margin: '2rem auto' }}>
        {profile.data.country === 'MEX' && !profile?.data?.company?.requireInvoice && (
          <Billing
            requireInvoice={profile?.data?.company?.requireInvoice}
            toProfile={() => setEditProfile(true)}
          />
        )}
      </Box>
      {isSelectedMethod.value && <HowMuchRecharge paymentMethod={isSelectedMethod.method} />}
      <GenericDrawer
        open={editProfile}
        onClose={() => setEditProfile(false)}
        title="Mis datos"
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <EditProfile openCompany />
      </GenericDrawer>
    </Box>
  );
};

const NOTICE_BG = '#FEF3C7';
const NOTICE_TEXT = '#D97706';

const Billing = ({ requireInvoice, toProfile }: BillingProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.75rem',
        maxWidth: '750px',
        padding: '1.25rem 1.5rem',
        borderRadius: '16px',
        backgroundColor: NOTICE_BG,
        color: NOTICE_TEXT,
        boxShadow: '0px 4px 12px rgba(217, 119, 6, 0.3)',
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: '1.5rem', mt: '0.15rem', flexShrink: 0 }} />
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: '0.5rem', color: 'inherit' }}>
          {requireInvoice ? 'Facturación activada' : 'Facturación desactivada'}
        </Typography>

        {requireInvoice ? (
          <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'inherit' }}>
            Mantén al día tus datos fiscales para generar tus facturas, las cuales podrás consultar
            dentro de{' '}
            <Box component="a" href="/invoices" sx={{ color: 'inherit', fontWeight: 700 }}>
              Mis Facturas.
            </Box>
            <br />
            <br />
            Cualquier cambio puedes realizarlo dentro de la sección{' '}
            <Box component="span" onClick={toProfile} sx={{ fontWeight: 700, cursor: 'pointer' }}>
              "De empresa"
            </Box>{' '}
            en tu Perfil.
          </Typography>
        ) : (
          <>
            <Typography
              sx={{ fontSize: '0.95rem', lineHeight: 1.6, mb: '0.75rem', color: 'inherit' }}
            >
              Tu cuenta no está configurada para emitir facturas. Para activarla:
            </Typography>
            <Box
              component="ol"
              sx={{
                margin: 0,
                mb: '0.75rem',
                pl: '1.25rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
              }}
            >
              <Typography
                component="li"
                sx={{ fontSize: '0.95rem', lineHeight: 1.5, color: 'inherit' }}
              >
                Ve a Perfil &gt;{' '}
                <Box
                  component="span"
                  onClick={toProfile}
                  sx={{ fontWeight: 700, cursor: 'pointer' }}
                >
                  De empresa
                </Box>
                .
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: '0.95rem', lineHeight: 1.5, color: 'inherit' }}
              >
                En <strong>&iquest;Necesitas Factura?</strong>, selecciona <strong>SÍ</strong>.
              </Typography>
              <Typography
                component="li"
                sx={{ fontSize: '0.95rem', lineHeight: 1.5, color: 'inherit' }}
              >
                Ingresa tus datos fiscales.
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'inherit' }}>
              Podrás consultar todos tus comprobantes en{' '}
              <Box component="a" href="/invoices" sx={{ color: 'inherit', fontWeight: 700 }}>
                Mis Facturas.
              </Box>
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default Balance;
